import { useEffect, useRef, useState } from "react";

/**
 * Reveal — fades + slides children into view when they enter the viewport.
 * Lightweight (IntersectionObserver, no deps). Respects prefers-reduced-motion.
 *
 * Props:
 *  - variant: "fade-up" (default) | "fade" | "slide-left" | "slide-right" | "zoom"
 *  - delay: ms delay before the animation kicks in
 *  - threshold: % of element visible to trigger (default 0.15)
 *  - as: element tag (default "div")
 *  - once: animate only once (default true)
 */
export default function Reveal({
  children,
  variant = "fade-up",
  delay = 0,
  threshold = 0.15,
  as: Tag = "div",
  once = true,
  className = "",
  style,
  ...rest
}) {
  const ref = useRef(null);
  // Always start invisible so prerender and hydration produce identical HTML.
  // If the user has prefers-reduced-motion set, we flip to visible in an effect
  // below (which runs only on the client after mount).
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) obs.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, once]);

  return (
    <Tag
      ref={ref}
      className={`reveal reveal-${variant}${visible ? " reveal-in" : ""} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms", ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
